/**
 * Einzelstück detail popup: fetches the rendered popup for a unit, injects it as a full-screen
 * overlay and wires its interactions — the unit switcher (re-fetches for another unit), the image
 * gallery, close handling and the "In den Warenkorb" action.
 *
 * The gallery is a Swiper (native touch swipe + arrows + dots); fullscreen is handled by the shared
 * full-picture-zoom component, triggered by the .js_full_picture_opener images via its own
 * document-level delegation, so this file owns no bespoke slider or lightbox logic.
 *
 * Opened from the condensed box via window.openUnitDetail(); loaded eagerly via requireScript on the
 * PDP (the injected popup markup can't trigger Twig's requireScript itself), so it self-initialises
 * through event delegation on the document.
 */
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { shareUrl } from './share.js';

(function () {
  const ENDPOINT = '/shopController.php';

  // Everything in the modal that describes the focused unit; the switcher rail between them stays.
  const UNIT_SECTIONS = ['.unit_detail__head', '.unit_detail__body', '.unit_detail__foot'];

  let gallerySwiper = null;

  // Distinct GTM event so we can measure how often customers buy straight from the Einzelstück
  // popup, separately from the regular buybox add-to-cart (which fires the standard add_to_cart).
  function trackAddToCart(articleId) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'unit_detail_add_to_cart',
      article_id: articleId,
    });
  }

  function fetchAndOpen(articleTypeId, okClass, articleId, newBattery, vatOnly) {
    if (!articleTypeId || !articleId) {
      return;
    }
    const params = new URLSearchParams({
      objective: 'getunitdetail',
      atid: articleTypeId,
      ok: okClass || '',
      aid: articleId,
      newbat: newBattery || '',
      no_diff: vatOnly || '',
    });
    fetch(ENDPOINT + '?' + params.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data && data.html) {
          render(data.html);
        }
      })
      .catch(function () {
        /* non-critical: the card stays usable */
      });
  }

  // The fetched fragment is the popup overlay plus its own Lucide sprite sheet, so it's hosted in a
  // wrapper rather than inserted as a single node.
  function render(html) {
    let host = document.getElementById('js_unit_detail_host');
    if (!host) {
      host = document.createElement('div');
      host.id = 'js_unit_detail_host';
      document.body.appendChild(host);
      document.body.classList.add('unit_detail_open');
      document.addEventListener('keydown', onKeydown);
      host.innerHTML = html;
      const popup = host.querySelector('.js_unit_detail');
      if (popup) {
        wire(popup);
        initGallery(popup);
      }
      return;
    }

    // Switching units: swap only the sections that describe the unit, so the overlay element — and
    // its already-wired click handler — survives. Re-injecting the whole fragment would replay the
    // fade/rise opening animations and make the box visibly flash on every switch. The Lucide sprite
    // from the first open stays in the host, so the new icons still resolve their <symbol>s.
    const currentModal = host.querySelector('.unit_detail__modal');
    const incoming = document.createElement('div');
    incoming.innerHTML = html;
    const incomingModal = incoming.querySelector('.unit_detail__modal');
    if (currentModal && incomingModal) {
      const popup = host.querySelector('.js_unit_detail');
      if (!swapUnitSections(currentModal, incomingModal)) {
        currentModal.replaceChildren(...incomingModal.childNodes);
      }
      const incomingPopup = incoming.querySelector('.js_unit_detail');
      if (popup && incomingPopup) {
        popup.dataset.articleId = incomingPopup.dataset.articleId;
      }
      initGallery(popup);
    } else {
      host.innerHTML = html;
      const popup = host.querySelector('.js_unit_detail');
      if (popup) {
        wire(popup);
        initGallery(popup);
      }
    }
  }

  // The switcher rail offers the same units no matter which one is in focus, so it is kept across a
  // switch and only its active chip moves: replacing the rail would reset its horizontal scroll
  // position and re-request every thumbnail, which makes comparing several units needlessly tedious.
  // Returns false when the incoming rail lists other units (a unit sold out, the list re-paged),
  // leaving the caller to swap the whole modal instead.
  function swapUnitSections(currentModal, incomingModal) {
    const currentRail = currentModal.querySelector('.unit_detail__switch');
    const incomingRail = incomingModal.querySelector('.unit_detail__switch');
    if (!currentRail || !incomingRail || railUnitIds(currentRail) !== railUnitIds(incomingRail)) {
      return false;
    }

    const sections = UNIT_SECTIONS.map(function (selector) {
      return {
        current: currentModal.querySelector(selector),
        incoming: incomingModal.querySelector(selector),
      };
    });
    if (
      sections.some(function (section) {
        return !section.current || !section.incoming;
      })
    ) {
      return false;
    }

    sections.forEach(function (section) {
      section.current.replaceWith(section.incoming);
    });

    const incomingActiveChip = incomingRail.querySelector('.unit_detail__sw--active');
    const activeArticleId = incomingActiveChip ? incomingActiveChip.dataset.articleId : null;
    currentRail.querySelectorAll('.js_unit_detail__sw').forEach(function (chip) {
      const isActive = activeArticleId !== null && chip.dataset.articleId === activeArticleId;
      chip.classList.toggle('unit_detail__sw--active', isActive);
      chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    return true;
  }

  function railUnitIds(rail) {
    return Array.from(rail.querySelectorAll('.js_unit_detail__sw'))
      .map(function (chip) {
        return chip.dataset.articleId;
      })
      .join(',');
  }

  // (Re)build the gallery Swiper. The unit switcher replaces the stage element, so any prior instance
  // is destroyed first. Single-image units need no slider — the lone image still opens fullscreen.
  function initGallery(popup) {
    if (gallerySwiper) {
      gallerySwiper.destroy(true, true);
      gallerySwiper = null;
    }
    if (!popup) {
      return;
    }
    const stage = popup.querySelector('.js_unit_detail__stage');
    if (!stage || stage.querySelectorAll('.swiper-slide').length < 2) {
      return;
    }
    gallerySwiper = new Swiper(stage, {
      modules: [Navigation, Pagination],
      slidesPerView: 1,
      loop: true,
      navigation: {
        prevEl: stage.querySelector('.js_unit_detail__prev'),
        nextEl: stage.querySelector('.js_unit_detail__next'),
      },
      pagination: {
        el: stage.querySelector('.js_unit_detail__dots'),
        clickable: true,
        bulletClass: 'unit_detail__dot',
        bulletActiveClass: 'unit_detail__dot--active',
      },
    });
  }

  function close() {
    if (gallerySwiper) {
      gallerySwiper.destroy(true, true);
      gallerySwiper = null;
    }
    const host = document.getElementById('js_unit_detail_host');
    if (host) {
      host.remove();
    }
    document.body.classList.remove('unit_detail_open');
    document.removeEventListener('keydown', onKeydown);
  }

  function onKeydown(event) {
    if (event.key !== 'Escape') {
      return;
    }
    // The shared fullscreen zoom is a native <dialog> and handles its own Escape; only close the
    // popup once the zoom is gone.
    const zoom = document.getElementById('js-full-picture-zoom');
    if (zoom && zoom.open) {
      return;
    }
    close();
  }

  function wire(popup) {
    const articleTypeId = popup.dataset.atid;
    const okClass = popup.dataset.ok;
    const newBattery = popup.dataset.newbat;
    const vatOnly = popup.dataset.vatonly;

    popup.addEventListener('click', function (event) {
      // Backdrop click (outside the modal) closes the popup.
      if (event.target === popup) {
        close();
        return;
      }
      if (event.target.closest('.js_unit_detail__close')) {
        close();
        return;
      }

      const shareButton = event.target.closest('.js_unit_detail__share');
      if (shareButton) {
        shareUrl(shareButton.dataset.url);
        return;
      }

      const switcherButton = event.target.closest('.js_unit_detail__sw');
      if (switcherButton) {
        fetchAndOpen(articleTypeId, okClass, switcherButton.dataset.articleId, newBattery, vatOnly);
        return;
      }

      const addButton = event.target.closest('.js_unit_detail__add');
      if (addButton) {
        trackAddToCart(addButton.dataset.articleId);
        window.selectedUnitArticleId = addButton.dataset.articleId;
        const unitPrice = Number(addButton.dataset.price);
        if (unitPrice > 0) {
          window.basePrice = unitPrice;
        }
        if (typeof window.buybox !== 'undefined' && typeof window.buybox.testAvailable === 'function') {
          window.buybox.testAvailable(addButton.dataset.articleId, false, newBattery === '1');
        }
      }
    });
  }

  window.openUnitDetail = fetchAndOpen;
})();
